import { useCallback, useState } from 'react'
import { QINIU_TOKEN } from '~/graphqls/gqls'
import { RcFile, UploadChangeParam } from 'antd/lib/upload'
import { LIST_IGNORE } from 'antd/lib/upload/Upload'
import { message } from 'antd'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { getBase64, getSm3HashOfFile } from '~/utils'
import { UploadFile } from 'antd/lib/upload/interface'
import { FileType } from '~/configs'
import { OssBucket } from '~/graphqls/types'

export interface ExtendsUploadFile extends UploadFile {
  sm3hash?: string
  url?: string
}

interface OptionsProps {
  onUrlChange?: (url: string, hash: string) => void
  /** 大小限制，单位 M */
  limit?: number
  needHash?: boolean
  // 默认值TRADE_PROTECT（开启原图保护），作用于作品
  bucket?: OssBucket
  /** 是否显示上传列表 */
  showList?: boolean
  /** 设置上传列表 */
  onFileListChange?: (fileList: ExtendsUploadFile[]) => void
  /** 剩余可上传数量 */
  remainToken?: number
  /** 支持文件类型 */
  fileTypes?: string[]
  dirname?: string
}

export const useUpload = (options?: OptionsProps) => {
  const {
    onUrlChange,
    limit = 2,
    needHash = false,
    bucket = OssBucket.TRADE_PROTECT,
    showList = false,
    onFileListChange,
    remainToken = Infinity,
    fileTypes = [FileType.jpg, FileType.png],
    dirname,
  } = options || {}

  const [token, setToken] = useState('')
  const [region, setRegion] = useState('')
  const [base64, setBase64] = useState('')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  let _remainToken = remainToken

  const getQiniuToken = useImperativeQuery(QINIU_TOKEN)

  const beforeUpload = useCallback(
    async (file: RcFile & { sm3hash?: string }) => {
      console.log(file.type, 'beforeUpload.file.type')
      if (!fileTypes.includes(file.type)) {
        message.destroy()
        message.error('不支持该文件类型')
        return showList ? LIST_IGNORE : Promise.reject()
      }

      const isLimit = file.size / 1024 / 1024 < limit
      if (!isLimit) {
        message.destroy()
        message.error(`请上传 ${limit}M 内的文件`)
        return showList ? LIST_IGNORE : Promise.reject()
      }

      if (needHash) {
        try {
          const hashStr = await getSm3HashOfFile(file)
          file.sm3hash = hashStr
        } catch (err) {
          return showList ? LIST_IGNORE : Promise.reject()
        }
      }

      const res = await getQiniuToken({
        bucket,
        dirName: dirname,
      }).then(data => {
        const upToken = data?.data?.upToken.token || ''
        const upRegion = data?.data?.upToken.region || ''

        if (!upToken || !upRegion) {
          message.destroy()
          message.error('上传地址异常')
        }

        setToken(upToken)
        setRegion(upRegion)
        return Promise.resolve()
      })

      _remainToken -= 1
      if (_remainToken < 0) {
        return showList ? LIST_IGNORE : Promise.reject()
      }

      return res
    },
    [getQiniuToken, limit, remainToken, dirname]
  )

  /** 对应 Upload 组件的 onChange 事件 */
  const onUpload = useCallback(
    (info: UploadChangeParam) => {
      if (info.file.status === 'done') {
        info.file.url = `${info.file.response?.baseUrl}/${info.file.response?.key}`
      }
      onFileListChange?.(info.fileList)

      if (info.file.status === 'uploading') {
        setProgress(Math.floor(info.file.percent || 0))
        setLoading(true)
        return
      }

      if (info.file.status === 'error') {
        setLoading(false)
        onUrlChange?.('', '')
        message.error('上传异常，请稍后重试')
        return
      }

      if (info.file.status === 'done') {
        setLoading(false)
        getBase64(info.file.originFileObj, imgUrl => {
          setBase64(imgUrl)
          onUrlChange?.(
            `${info.file.response?.baseUrl}/${info.file.response?.key}`,
            (info.file as any).sm3hash || ''
          )
          info.file.url = `${info.file.response?.baseUrl}/${info.file.response?.key}`
          info.file.originFileObj = undefined
          info.file.response = undefined
        })
      }
    },
    [remainToken, onFileListChange]
  )

  const QNUPLOAD = `https://upload-${region}.qiniup.com`

  return {
    token,
    region,
    loading,
    beforeUpload,
    onUpload,
    base64,
    progress,
    QNUPLOAD,
  }
}
